/**
 * Shared frontend time helpers.
 *
 * Contract:
 * - Backend instants are UTC ISO strings, preferably with Z.
 * - Browser-local Date objects are only used for display and user input.
 * - Wall-clock schedule intent should include date/time plus an IANA timezone.
 */

const ISO_WITH_TIMEZONE = /([zZ]|[+-]\d{2}:\d{2})$/;
const ISO_WITHOUT_TIMEZONE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

export const LOCAL_TIMEZONE_PREFERENCE = 'local';

const US_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Phoenix',
  'America/Los_Angeles',
];

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function getTimeZoneDateTimeParts(date: Date, timeZone: string): DateTimeParts | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);

    const values = Object.fromEntries(
      parts
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, Number(part.value)]),
    );

    return {
      year: values.year,
      month: values.month,
      day: values.day,
      hour: values.hour,
      minute: values.minute,
      second: values.second,
    };
  } catch {
    return null;
  }
}

function getTimeZoneOffsetMs(timeZone: string, date: Date): number {
  const parts = getTimeZoneDateTimeParts(date, timeZone);
  if (!parts) return 0;

  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    date.getMilliseconds(),
  );

  return asUtc - date.getTime();
}

export function parseUtcInstant(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : null;
  if (typeof value !== 'string') return null;

  // Normalize "YYYY-MM-DD HH:MM:SS" (common DB dump form) to ISO with T.
  const text = value.trim().replace(/^(\d{4}-\d{2}-\d{2})[ ](\d)/, '$1T$2');
  if (!text) return null;

  const normalized = ISO_WITH_TIMEZONE.test(text)
    ? text
    : ISO_WITHOUT_TIMEZONE.test(text)
      ? `${text}Z`
      : text;

  const date = new Date(normalized);
  return Number.isFinite(date.getTime()) ? date : null;
}

export function getBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function getPreferredTimeZone(preference?: string | null): string {
  return preference && preference !== LOCAL_TIMEZONE_PREFERENCE
    ? preference
    : getBrowserTimeZone();
}

export function getTimeZoneOptions(): string[] {
  const supportedValuesOf = (Intl as typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[];
  }).supportedValuesOf;
  const supportedTimeZones = typeof supportedValuesOf === 'function'
    ? supportedValuesOf('timeZone')
    : US_TIMEZONES;
  const supportedTimeZoneSet = new Set(supportedTimeZones);

  return Array.from(
    new Set(US_TIMEZONES.filter((timeZone) => supportedTimeZoneSet.has(timeZone))),
  );
}

export function wallClockDateInTimeZoneToUtcDate(value: Date, timeZone: string): Date {
  if (!Number.isFinite(value.getTime())) return value;

  const utcGuess = Date.UTC(
    value.getFullYear(),
    value.getMonth(),
    value.getDate(),
    value.getHours(),
    value.getMinutes(),
    value.getSeconds(),
    value.getMilliseconds(),
  );

  const firstPass = utcGuess - getTimeZoneOffsetMs(timeZone, new Date(utcGuess));
  const secondPass = utcGuess - getTimeZoneOffsetMs(timeZone, new Date(firstPass));
  return new Date(secondPass);
}

export function wallClockDateInTimeZoneToUtcIso(value: Date, timeZone: string): string {
  return wallClockDateInTimeZoneToUtcDate(value, timeZone).toISOString();
}

export function ymdInTimeZoneToUtcInterval(
  ymd: string,
  timeZone: string,
): { startUtc: string; endUtc: string } | null {
  const [year, month, day] = ymd.split('-').map(Number);
  if (!year || !month || !day) return null;

  const startWall = new Date(year, month - 1, day, 0, 0, 0, 0);
  const endWall = new Date(year, month - 1, day, 23, 59, 59, 999);
  return {
    startUtc: wallClockDateInTimeZoneToUtcIso(startWall, timeZone),
    endUtc: wallClockDateInTimeZoneToUtcIso(endWall, timeZone),
  };
}

export function formatLocalDateTime(
  value: unknown,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  },
): string {
  const date = parseUtcInstant(value);
  return date ? date.toLocaleString(undefined, options) : '';
}

export function formatUtcInstant(
  value: unknown,
  options?: Intl.DateTimeFormatOptions,
): string {
  return formatLocalDateTime(value, options);
}

export function formatUtcInstantInTimeZone(
  value: unknown,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  },
): string {
  const date = parseUtcInstant(value);
  return date ? date.toLocaleString(undefined, { ...options, timeZone }) : '';
}

export function formatRelativeTimeFromNow(value: unknown, now: Date = new Date()): string {
  const date = parseUtcInstant(value);
  if (!date) return 'Not scheduled';

  const diff = date.getTime() - now.getTime();
  if (diff < 0) return 'Overdue';
  if (diff < 3600000) return `in ${Math.round(diff / 60000)} min`;
  if (diff < 86400000) return `in ${Math.round(diff / 3600000)} hr`;
  return `in ${Math.round(diff / 86400000)} days`;
}

export function formatLocalDate(
  value: unknown,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
): string {
  const date = parseUtcInstant(value);
  return date ? date.toLocaleDateString(undefined, options) : '';
}

export function localDatePartsToYmd(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function utcInstantToYmdInTimeZone(value: unknown, timeZone: string): string {
  const date = parseUtcInstant(value);
  if (!date) return '';
  const parts = getTimeZoneDateTimeParts(date, timeZone);
  if (!parts) return '';
  const yyyy = String(parts.year).padStart(4, '0');
  const mm = String(parts.month).padStart(2, '0');
  const dd = String(parts.day).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function localTimeInputToHms(value: string): string {
  const [hours = '00', minutes = '00'] = value.split(':');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
}

export function wallClockTimeToLocalDate(value: string): Date {
  const [hours = '0', minutes = '0', seconds = '0'] = value.split(':');
  const date = new Date();
  date.setHours(Number(hours) || 0, Number(minutes) || 0, Number(seconds) || 0, 0);
  return date;
}

export function localDateToWallClockTime(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}:00`;
}

export function formatWallClockTime(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  },
): string {
  if (!value) return '';
  const date = wallClockTimeToLocalDate(value);
  return date.toLocaleTimeString('en-US', options);
}

export function utcIsoToDateTimeInput(value: unknown): string {
  const date = parseUtcInstant(value);
  if (!date) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

export function localDateTimeInputToUtcIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}
